import React from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import {
  getRuler3DDelta,
  getRuler3DDirection,
} from '../../../../features/floorplan3d/measurements/measurement3DGeometry';
import { Ruler3DType } from '../../../../types/floorplannerTypes';
import DimensionField from '../components/DimensionField';
import NumberField from '../components/NumberField';
import UnitHeader from '../components/UnitHeader';
import { toDisplayLength } from '../propertyPanelUtils';
import type { PropertyPanelController } from '../types';

type Ruler3DSectionProps = Pick<PropertyPanelController, 'ruler3DDistance' | 'setRuler3DDistance' | 'updateGlobalState'> & {
  selectedRulers3D: Ruler3DType[];
  displayedRulers3D: Ruler3DType[];
  commonRuler3DDistance?: number;
};

const formatDirection = (ruler: Ruler3DType): string => {
  const direction = getRuler3DDirection(ruler);
  return `(${direction.x.toFixed(3)}, ${direction.y.toFixed(3)}, ${direction.z.toFixed(3)})`;
};

const Ruler3DSection: React.FC<Ruler3DSectionProps> = observer(({
  selectedRulers3D,
  displayedRulers3D,
  commonRuler3DDistance,
  ruler3DDistance,
  setRuler3DDistance,
  updateGlobalState,
}) => {
  const { t } = useTranslation();
  const mixed = t('common.mixed');

  return (
    <>
      {displayedRulers3D.map((ruler) => {
        const delta = getRuler3DDelta(ruler);

        return (
          <div key={`ruler3D-${ruler.id}`}>
            <div className="text-sm font-normal p-3">
              <UnitHeader title={selectedRulers3D.length > 1 ? t('propertyPanel.multipleRulers3D', { count: selectedRulers3D.length }) : t('propertyPanel.ruler3D')} />
              <div className="flex items-center justify-between py-2">
                <DimensionField
                  id="ruler3DDistance"
                  label={t('common.distance')}
                  value={selectedRulers3D.length > 1 ? (commonRuler3DDistance ?? '') : ruler3DDistance}
                  placeholder={selectedRulers3D.length > 1 ? mixed : undefined}
                  onChange={(nextValue) => {
                    const parsedValue = parseFloat(nextValue);
                    if (nextValue === '' || isNaN(parsedValue) || parsedValue < 1) {
                      setRuler3DDistance('');
                      return;
                    }

                    setRuler3DDistance(parsedValue);
                    updateGlobalState(selectedRulers3D, 'ruler3DDistance', parsedValue);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && ruler3DDistance !== '') {
                      updateGlobalState(selectedRulers3D, 'ruler3DDistance', ruler3DDistance);
                    }
                  }}
                />
              </div>
              <div className="grid grid-cols-3 gap-2 py-2">
                <DimensionField label={t('propertyPanel.deltaX')} value={Math.round(toDisplayLength(delta.x))} readOnly />
                <DimensionField label={t('propertyPanel.deltaY')} value={Math.round(toDisplayLength(delta.y))} readOnly />
                <DimensionField label={t('propertyPanel.deltaZ')} value={Math.round(toDisplayLength(delta.z))} readOnly />
              </div>
              <div className="flex items-center justify-between py-2">
                <NumberField label={t('propertyPanel.direction')} value={formatDirection(ruler)} readOnly type="text" />
              </div>
            </div>
            <div className="border-b border-gray-300" />
          </div>
        );
      })}
    </>
  );
});

export default Ruler3DSection;
